import { Select, Stack, tokens, Typography } from '@cast/design-system';
import {
  ChartMetric,
  ChartMetricsRowSkeleton,
} from '@cast/features-cluster-ui';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { formatCurrency, getCurrencyPrecision } from '@cast/utils';

import { DateRangeUnsupportedBanner } from '../../_components/DateRangeUnsupportedBanner';
import { WidgetLayout } from '../../../../_components/ui/WidgetLayout';

import { ResourceCostChart } from './ResourceCostChart';
import type { WorkloadResourceCostChartPoint } from '../_hooks/useWorkloadResourceCostChartData';

type CostRate = 'hourly' | 'daily' | 'monthly';

const COST_RATE_OPTIONS: { value: CostRate; label: string }[] = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'monthly', label: 'Monthly' },
];

type Props = {
  title: string;
  testId: string;
  metricLabel: string;
  metricInfoTooltip: string;
  metricTestId: string;
  failedToLoadTitle: string;
  hasOfferingBreakdown: boolean;
  costRate: CostRate;
  onCostRateChange: (rate: CostRate) => void;
  data: WorkloadResourceCostChartPoint[];
  averageCost: number;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  timeZone: string;
  isRangeSupported: boolean;
  chartTestId?: string;
  tooltipTestId?: string;
  unsupportedRangeTestId?: string;
};

export const ResourceCostChartWidget = ({
  title,
  testId,
  metricLabel,
  metricInfoTooltip,
  metricTestId,
  failedToLoadTitle,
  hasOfferingBreakdown,
  costRate,
  onCostRateChange,
  data,
  averageCost,
  isLoading,
  error,
  refetch,
  timeZone,
  isRangeSupported,
  chartTestId,
  tooltipTestId,
  unsupportedRangeTestId,
}: Props) => {
  const hasError = !!error;
  const hasData = data.some((point) => point.normalized !== null);

  const metricUnit =
    costRate === 'monthly' ? '/mo' : costRate === 'hourly' ? '/hr' : '/day';

  const headerEndContent = (
    <Select
      options={COST_RATE_OPTIONS}
      optionLabel="label"
      optionValue="value"
      value={costRate}
      onChange={(_, value) => {
        if (value) {
          onCostRateChange(value);
        }
      }}
      size="sm"
      prefix={
        <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
          Cost rate:
        </Typography>
      }
      inputProps={{
        sx: { width: 180 },
      }}
    />
  );

  return (
    <WidgetLayout
      title={title}
      testId={testId}
      headerEndContent={headerEndContent}
    >
      {!isRangeSupported ? (
        <DateRangeUnsupportedBanner testId={unsupportedRangeTestId} />
      ) : hasError ? (
        <FailedToLoad title={failedToLoadTitle} refresh={refetch} />
      ) : (
        <Stack gap={16} height="100%">
          {isLoading ? (
            <ChartMetricsRowSkeleton
              alwaysRow
              count={1}
              testId={`${testId}-metrics-skeleton`}
            />
          ) : (
            <ChartMetric
              testId={metricTestId}
              size="m"
              label={metricLabel}
              value={formatCurrency(averageCost, {
                maximumFractionDigits: getCurrencyPrecision(averageCost),
              })}
              unit={metricUnit}
              infoTooltip={metricInfoTooltip}
            />
          )}
          <ResourceCostChart
            data={data}
            isLoading={isLoading}
            hasNoData={!hasData}
            timeZone={timeZone}
            testId={chartTestId}
            tooltipTestId={tooltipTestId}
            hasOfferingBreakdown={hasOfferingBreakdown}
          />
        </Stack>
      )}
    </WidgetLayout>
  );
};
